import { supabase } from './supabase';

// ==================== THRIFT ITEMS ====================

export async function getThriftItems() {
  const { data, error } = await supabase
    .from('thrift_items')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching thrift items:', error);
    throw error;
  }
  return data;
}

export async function createThriftItem(item: {
  title: string;
  description: string;
  price: number;
  category?: string;
  condition?: string;
  image_url?: string;
}) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('thrift_items')
    .insert([
      {
        title: item.title,
        description: item.description,
        price: item.price,
        category: item.category,
        condition: item.condition,
        image_url: item.image_url,
        user_id: user.id,
        user_email: user.email,
        status: 'active'
      }
    ])
    .select();
  
  if (error) throw error;
  return data[0];
}

export async function deleteThriftItem(id: string) {
  const { error } = await supabase
    .from('thrift_items')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting thrift item:', error);
    throw error;
  }
}

// ==================== LOST & FOUND ITEMS ====================

export async function getLostFoundItems() {
  const { data, error } = await supabase
    .from('lost_found_items')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching lost/found items:', error);
    throw error;
  }
  return data;
}

export async function createLostFoundItem(item: {
  title: string;
  description: string;
  type: 'lost' | 'found';
  location: string;
  date: string;
  image_url?: string;
}) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('lost_found_items')
    .insert([
      {
        title: item.title,
        description: item.description,
        type: item.type,
        location: item.location,
        date: item.date,
        image_url: item.image_url,
        user_id: user.id,
        user_email: user.email,
        status: 'active'
      }
    ])
    .select();
  
  if (error) {
    console.error('Error creating lost/found item:', error);
    throw error;
  }
  return data[0];
}


export async function deleteLostFoundItem(id: string) {
  const { error } = await supabase
    .from('lost_found_items')
    .delete()
    .eq('id', id);
  
  if (error) {
    console.error('Error deleting lost/found item:', error);
    throw error;
  }
}

// ==================== IMAGE UPLOAD ====================

export async function uploadItemImage(file: File): Promise<string | null> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { data, error } = await supabase.storage
      .from('item-images')
      .upload(filePath, file, {
        contentType: file.type || undefined,
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('ERROR Upload error:', error);
      throw error;
    }

    console.log('INFO Upload successful:', data);

    const { data: { publicUrl } } = supabase.storage
      .from('item-images')
      .getPublicUrl(filePath);

    console.log('INFO Public URL:', publicUrl);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
}

// ==================== GET USER'S OWN POSTS ====================

export async function getMyThriftItems() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('thrift_items')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching my thrift items:', error);
    throw error;
  }
  return data;
}

export async function getMyLostFoundItems() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('lost_found_items')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching my lost/found items:', error);
    throw error;
  }
  return data;
}

export async function updateThriftItem(id: string, updates: Partial<{
  title: string;
  description: string;
  price: number;
  category: string;
  condition: string;
  image_url?: string;
}>) {
  const { data, error } = await supabase
    .from('thrift_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateLostFoundItem(id: string, updates: Partial<{
  title: string;
  description: string;
  location: string;
  date: string;
  type: 'lost' | 'found';
  image_url?: string;
}>) {
  const { data, error } = await supabase
    .from('lost_found_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ==================== EMAIL FUNCTIONS ====================

interface NewItemEmailParams {
  to: string[];
  itemType: 'lost' | 'found' | 'thrift';
  itemTitle: string;
  itemDescription: string;
  itemLocation?: string;
  itemPrice?: number;
  posterEmail: string;
  itemUrl: string;
}

interface ContactEmailParams {
  to: string;
  subject: string;
  message: string;
  replyTo?: string;
}

//escapes HTML characters in the input string 
function escapeHtml(input: string): string { 
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

//sends Contact messages through the API(Resend), using the current user's auth token
export async function sendContactEmail(params: ContactEmailParams) {
  const { to, subject, message, replyTo } = params;

  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  const senderEmail = session?.user?.email || "Unknown";

  if (!accessToken) {
    throw new Error("Not authenticated");
  }

  const safeSubject = escapeHtml(subject);
  const safeMessage = escapeHtml(message).replace(/\n/g, "<br>");
  const safeSender = escapeHtml(senderEmail);

  //creates the HTML body of the email
  const htmlBody = `
    <!DOCTYPE html>
    <html>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <h2 style="color: #003f87; margin-bottom: 16px;">Find On LU Contact Message</h2>
          <p style="margin: 0 0 12px;"><strong>From:</strong> ${safeSender}</p>
          <p style="margin: 0 0 12px;"><strong>Subject:</strong> ${safeSubject}</p>
          <div style="margin-top: 12px; padding: 12px; background: #f8fafc; border-radius: 8px;">
            ${safeMessage}
          </div>
          <p style="margin-top: 16px; font-size: 12px; color: #64748b;">
            This message was sent through Find On LU.
          </p>
        </div>
      </body>
    </html>
  `;

  const response = await fetch("/api/send-email", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      to: [to],
      subject,
      html: htmlBody,
      replyTo: replyTo || senderEmail,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to send contact email");
  }

  return response.json();
}

export async function sendNewItemNotification(params: NewItemEmailParams) {
  const {
    to,
    itemType,
    itemTitle,
    itemDescription,
    itemLocation,
    itemPrice,
    posterEmail,
    itemUrl,
  } = params;

  const subject = 
    itemType === 'thrift' 
      ? `🛍️ New Item for Sale: ${itemTitle}` 
      : `🔍 New ${itemType === 'lost' ? 'Lost' : 'Found'} Item: ${itemTitle}`;

  const htmlBody = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9; }
          .header { background-color: #003f87; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .content { background-color: white; padding: 30px; border-radius: 0 0 8px 8px; }
          .badge { display: inline-block; padding: 5px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; margin-bottom: 10px; }
          .lost-badge { background-color: #fee; color: #c00; }
          .found-badge { background-color: #efe; color: #060; }
          .thrift-badge { background-color: #eef; color: #006; }
          .button { display: inline-block; padding: 12px 24px; background-color: #003f87; color: white; text-decoration: none; border-radius: 6px; margin-top: 20px; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Find On LU</h1>
            <p>New Item Posted</p>
          </div>
          <div class="content">
            <span class="badge ${itemType === 'lost' ? 'lost-badge' : itemType === 'found' ? 'found-badge' : 'thrift-badge'}">
              ${itemType.toUpperCase()}
            </span>
            <h2>${itemTitle}</h2>
            ${itemPrice ? `<p style="font-size: 24px; color: #060; font-weight: bold;">$${itemPrice}</p>` : ''}
            <p><strong>Description:</strong></p>
            <p>${itemDescription}</p>
            ${itemLocation ? `<p><strong>Location:</strong> ${itemLocation}</p>` : ''}
            <p><strong>Posted by:</strong> ${posterEmail.split('@')[0]}</p>
            <a href="${itemUrl}" class="button">View Item</a>
            <p style="margin-top: 30px; font-size: 12px; color: #666;">
              Want to contact the poster? Click "View Item" and use the contact button.
            </p>
          </div>
          <div class="footer">
            <p>You're receiving this because you're a member of Find On LU.</p>
            <p>Lawrence University Campus Marketplace</p>
          </div>
        </div>
      </body>
    </html>
  `;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        to: to,
        subject: subject,
        html: htmlBody,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Email send error:', errorData);
      throw new Error(errorData.error || 'Failed to send email');
    }

    const data = await response.json();
    console.log('INFO Email sent successfully:', data);
    return data;

  } catch (error) {
    console.error('ERROR Failed to send email:', error);
    throw error;
  }
}

export async function getAllUserEmails(): Promise<string[]> {
  try {
    const { data: lostFoundUsers } = await supabase
      .from('lost_found_items')
      .select('user_email');

    const { data: thriftUsers } = await supabase
      .from('thrift_items')
      .select('user_email');

    const allEmails: string[] = [];
    
    if (lostFoundUsers) {
      lostFoundUsers.forEach((u: any) => {
        if (u.user_email && typeof u.user_email === 'string') {
          allEmails.push(u.user_email);
        }
      });
    }
    
    if (thriftUsers) {
      thriftUsers.forEach((u: any) => {
        if (u.user_email && typeof u.user_email === 'string') {
          allEmails.push(u.user_email);
        }
      });
    }

    const uniqueEmails = Array.from(new Set(allEmails));
    
    console.log(`INFO Found ${uniqueEmails.length} unique users`);
    return uniqueEmails;
  } catch (error) {
    console.error('Error getting user emails:', error);
    return [];
  }
}

// ==================== AI MATCHING FUNCTIONS ====================

// Client-side matching is disabled. Use /api/match-items.
export async function findMatchingLostItems(foundItem: {
  title: string;
  description: string;
  location?: string;
  image_url?: string; 
}): Promise<any[]> {
  void foundItem;
  throw new Error('Client-side matching is disabled. Use /api/match-items.');
}

export async function notifyMatchedUsers(
  matches: any[],
  foundItem: {
    title: string;
    description: string;
    location?: string;
    user_email: string;
    image_url?: string;
  }
) {
  console.log(`INFO Notifying ${matches.length} matched users`);

  for (const match of matches) {
    try {
      const matchPercent = Math.round(match.score * 100);
      const confidence = match.confidence || 'Good';
      
      console.log(`INFO Sending email to ${match.item.user_email} (${matchPercent}% match - ${confidence} confidence)`);

      let subject = '';
      if (confidence === 'Very High') {
        subject = `🎯 Very High Match (${matchPercent}%): Your lost item likely found!`;
      } else if (confidence === 'High') {
        subject = `✅ High Match (${matchPercent}%): Possible match for your lost item`;
      } else {
        subject = `🔍 Good Match (${matchPercent}%): Check if this matches your item`;
      }

      let description = `Someone found an item that matches your lost "${match.item.title}"!\n\n`;
      description += `Match Confidence: ${confidence} (${matchPercent}%)\n\n`;

      if (confidence === 'Very High') {
        description += `This is a very strong match! `;
      } else if (confidence === 'High') {
        description += `This looks like a good match. `;
      } else {
        description += `This might be your item - please verify the details carefully. `;
      }

      description += `\n\nFound Item Details:\n`;
      description += `- Title: ${foundItem.title}\n`;
      description += `- Description: ${foundItem.description}\n`;
      description += `- Location: ${foundItem.location || 'Not specified'}\n\n`;
      description += `Please check the details carefully to confirm if this is your item.`;

      await sendNewItemNotification({
        to: [match.item.user_email],
        itemType: 'found',
        itemTitle: subject,
        itemDescription: description,
        itemLocation: foundItem.location,
        posterEmail: foundItem.user_email,
        itemUrl: `${window.location.origin}/lost-found`,
      });

      console.log(`INFO Email sent to ${match.item.user_email}`);
    } catch (error) {
      console.error(`ERROR Failed to notify ${match.item.user_email}:`, error);
    }
  }

  console.log('INFO All notification emails sent');
}

export async function updateItemStatus(itemId: string, status: 'active' | 'reunited') {
  const { data, error } = await supabase
    .from('lost_found_items')
    .update({ status })
    .eq('id', itemId)
    .select()
    .single();

  if (error) {
    console.error('Error updating status:', error);
    throw error;
  }

  return data;
}

export async function updateThriftStatus(
  itemId: string,
  status: 'active' | 'pending' | 'sold'
) {
  const { data, error } = await supabase
    .from('thrift_items')
    .update({ status })
    .eq('id', itemId)
    .select()
    .single();

  if (error) {
    console.error('Error updating thrift status:', error);
    throw error;
  }

  return data;
}